/* eslint-disable no-unsafe-optional-chaining */
import i18n from '@_plugins/i18n';
import { container } from '@_plugins/ioc';
import LaravelAPI from '@data/api/laravel.api';
import { toSortParams } from '@data/dto/api.dto';
import {
  toDeviceType,
  toDeviceTypeFilterParams,
} from '@data/dto/device-type.dto';
import { Meta, PaginateResult, SortParams } from '@data/models/api.interface';
import { DatatableReorderParams } from '@data/models/datatable.interface';
import {
  DeviceType,
  DeviceTypeHttp,
  DeviceTypeWizard,
} from '@data/models/device-type.interface';
import { FilterParams } from '@data/models/filter.interface';
import ErrorService from 'core/error.service';
import { injectable } from 'inversify';

@injectable()
export default class DeviceTypeService {
  private API = container.get<LaravelAPI>(LaravelAPI);
  private readonly deviceTypeEndpoint = '/device-type-repositories';
  private errorService = container.get<ErrorService>(ErrorService);

  public async listDeviceTypes(
    filters: FilterParams,
  ): Promise<PaginateResult<DeviceType[]>> {
    const { data: response, error } = await this.API?.get<{
      data: DeviceTypeHttp[];
      meta: Meta;
    }>(this.deviceTypeEndpoint, toDeviceTypeFilterParams(filters));

    if (error) {
      this.errorService.handleHttpError(error);
      return {
        items: [],
        totalSize: 0,
      };
    }

    return {
      items: response?.data?.map(toDeviceType) || [],
      totalSize: response?.meta.page.total || 0,
    };
  }

  public async upsertDeviceType({
    id,
    ...deviceTypeWizard
  }: DeviceTypeWizard): Promise<DeviceType | void> {
    if (!id) {
      const { data, error } = await this.API?.post<
        { data: Omit<DeviceTypeWizard, 'id'> },
        { data: DeviceTypeHttp }
      >(this.deviceTypeEndpoint, { data: deviceTypeWizard });

      if (error) {
        this.errorService.handleHttpError(error);
        return;
      }

      return toDeviceType(data?.data || {});
    }

    const { data, error } = await this.API?.patch<
      { data: DeviceTypeWizard },
      { data: DeviceTypeHttp }
    >(`${this.deviceTypeEndpoint}/${id}`, {
      data: { ...deviceTypeWizard, id },
    });

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toDeviceType(data?.data || {});
  }

  public async deleteDeviceType(id: string): Promise<DeviceType | void> {
    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.deleting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { data, error } = await this.API?.delete<{
      data: DeviceTypeHttp;
    }>(`${this.deviceTypeEndpoint}/${id}`);

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toDeviceType(data?.data || {});
  }

  public async sortDeviceType(
    sortParams: DatatableReorderParams<DeviceType>,
  ): Promise<DeviceType | void> {
    const {
      current: { id },
    } = sortParams;

    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.sorting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { error } = await this.API?.post<{ data: SortParams }, {}>(
      `${this.deviceTypeEndpoint}/${id}/sort`,
      {
        data: toSortParams('device_type_repositories', sortParams),
      },
    );

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return sortParams.current;
  }
}
